import { BidAlerts } from '@components/bid-buy/BidAlerts';
import BidBuyHeadline from '@components/bid-buy/BidBuyHeadline';
import { BidBuySubmit } from '@components/bid-buy/BidBuySubmit';
import BidBuyTimer from '@components/bid-buy/BidBuyTimer';
import { KVPSSelect } from '@components/bid-buy/KVPSSelect';
import { TransportSelectPackage } from '@components/bid-buy/TransportSelect';
import DeliveryAddressSelect from '@components/dealer-table/DeliveryAddressSelect';
import { Grid, Paper } from '@material-ui/core';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetTransportOptionsForPackage } from 'src/hooks/useTransportOptions';

import BidForm from '../components/bid-buy/BidForm';
import useBidBuyStyles from '../components/bid-buy/useBidBuyStyles';
import CarDetailHeadline from '../components/CarDetailHeadline';
import DeleteBidDialog from '../components/DeleteBidDialog';
import { noKVPSSources } from '../hooks/useBidBuyForm';
import { useBidBuyFormPackage } from '../hooks/useBidBuyFormPackage';
import { FetchCalculationParams } from '../hooks/useCalculation';
import {
  useAccountDealerId,
  getBidBuyErrors,
  getBiddingStatus,
  selectDefaultTransportOption,
} from '../modules/bid-buy-helpers';
import {
  BidDTO,
  BidSuccessStatus,
  DetailCarDTO,
  KvpsType,
  MarketingChannel,
  PackageDetails,
  SourceType,
  SupportedPriceType,
  ValidityType,
} from '../modules/generated/api';
import { setToArray } from '../modules/util';

type BidBuyPackageProps = {
  packageDetails: PackageDetails;
  refresh: () => Promise<void> | void;
};

const BidBuyPackage = ({ packageDetails, refresh }: BidBuyPackageProps) => {
  const classes = useBidBuyStyles();
  const { t } = useTranslation();

  const {
    form: { control, setValue, watch, getValues },
    form,
    resetAndRefetch,
    fetchCalculation,
  } = useBidBuyFormPackage(packageDetails);

  const watchKvps = watch('kvps');
  const watchDeliveryAddress = watch('deliveryAddress');
  // watch selectedTransportOption to keep BidBuySubmit's getValues in sync on re-render
  watch('selectedTransportOption');
  const noCentralKvpsSelected = packageDetails.kvps
    ? packageDetails.kvps.find((k) => k.kvps === watchKvps)?.type !== KvpsType.Central
    : true;

  const { data: transportOptionsData, isLoading: transportOptionsLoading } = useGetTransportOptionsForPackage(
    packageDetails.source || SourceType.Vwfs,
    packageDetails.packageId || '',
    watchDeliveryAddress || '',
    watchKvps || '',
  );

  useEffect(() => {
    setValue('selectedTransportOption', '');
  }, [watchDeliveryAddress, setValue]);

  // auto-select transport option using same logic as single-car flow
  useEffect(() => {
    if (
      packageDetails.source === SourceType.Vwfs &&
      getValues('selectedTransportOption') === '' &&
      transportOptionsData?.options
    ) {
      // map to TransportOptionDTO shape so we can reuse selectDefaultTransportOption
      const mapped = transportOptionsData.options.map((o) => ({ id: o.id, provider: o.transportationProvider }));
      setValue(
        'selectedTransportOption',
        selectDefaultTransportOption(mapped as Parameters<typeof selectDefaultTransportOption>[0]),
        { shouldValidate: true },
      );
    }
  }, [packageDetails.source, getValues, setValue, transportOptionsData]);

  const fetchCalculationForBidForm = useCallback(
    (params: FetchCalculationParams) =>
      fetchCalculation({ ...params, fixedVariable: params.fixedVariable as unknown as SupportedPriceType }),
    [fetchCalculation],
  );

  const [bidForDeletion, setBidForDeletion] = useState<BidDTO | undefined>();
  const [bidDeletePending, setBidDeletePending] = useState(false);
  const currentDealerIds = useAccountDealerId();
  const actualBidSuccessState = getBiddingStatus(packageDetails.bid);
  const [bidEditMode, setBidEditMode] = useState(true);

  const marketingChannel = packageDetails.marketingChannel || [];
  const supportsBidding = marketingChannel.includes(MarketingChannel.Bid);
  const supportsBuying = marketingChannel.includes(MarketingChannel.Fixed);
  // Bid automation is not supported for packages
  const supportsBidAutomation = false;
  const isNoKVPSCar = noKVPSSources.includes(packageDetails.source!);
  const showKvps = !!packageDetails.kvps?.length && !isNoKVPSCar;

  const errors = getBidBuyErrors({ isNoKVPSCar, kvps: packageDetails.kvps });
  const hasErrors = !isEmpty(errors);

  const allowedDeliveryAddressIds = packageDetails.deliveryAddressSelection?.selectedAddressByKVPS?.[watchKvps] || [];

  useEffect(() => {
    if (bidDeletePending && !packageDetails.bid?.bids?.length) {
      setBidDeletePending(false);
    }
  }, [bidDeletePending, packageDetails.bid?.bids]);

  useEffect(() => {
    setBidEditMode(
      !(
        actualBidSuccessState === BidSuccessStatus.Bought ||
        actualBidSuccessState === BidSuccessStatus.Sent ||
        actualBidSuccessState === BidSuccessStatus.Scheduled
      ) && !hasErrors,
    );
  }, [actualBidSuccessState, hasErrors]);

  const toggleEdit = () => {
    if (bidEditMode) resetAndRefetch();
    setBidEditMode((prevState) => !prevState);
  };
  const onClose = () => setBidForDeletion(undefined);

  const carForComponents: DetailCarDTO = {
    source: packageDetails.source,
    packageId: packageDetails.packageId,
    carId: packageDetails.carIds ? packageDetails.carIds[0] : undefined, // we send both package-id and first CarId just to be safe
    bid: packageDetails.bid,
    kvps: packageDetails.kvps ?? [],
    deliveryAddressSelection: packageDetails.deliveryAddressSelection,
    marketingChannel: packageDetails.marketingChannel,
    dateEnd: packageDetails.dateEnd,
    previousAuctions: packageDetails.previousAuctions,
    potential: packageDetails.potential,
    bidStateInfo: { allHistoricBids: packageDetails.bid?.bids },
    virtualSources: packageDetails.virtualSources,
    validity: ValidityType.Current,
  };

  return (
    <FormProvider {...form}>
      <Paper elevation={3} className={classes.bidBuy}>
        <div className={classes.headlineWrapper}>
          {!!packageDetails.bid?.bids?.length && !bidDeletePending && (
            <BidBuyHeadline
              bidEditMode={bidEditMode}
              actualBidSuccessState={actualBidSuccessState}
              toggleEdit={toggleEdit}
              onDelete={() => {
                const sameDealerOtherBids = (carForComponents.bidStateInfo?.otherCurrentBids ?? []).filter((bid) =>
                  currentDealerIds?.includes(bid.dealer?.id ?? ''),
                );
                setBidForDeletion({
                  ...packageDetails.bid!,
                  bids: [...(packageDetails.bid?.bids ?? []), ...sameDealerOtherBids],
                });
              }}
            />
          )}
          <div className={classes.header}>
            <CarDetailHeadline
              title={`${
                (supportsBidding && actualBidSuccessState !== BidSuccessStatus.Bought && t('common.toBid')) || ''
              }${
                (supportsBidding && supportsBuying && actualBidSuccessState !== BidSuccessStatus.Bought && ' & ') || ''
              }${(supportsBuying && t('common.toBuy')) || ''}`}
            />
            <div>
              <Grid container direction="column">
                {showKvps && (
                  <Grid item>
                    <KVPSSelect
                      rules={{ required: showKvps }}
                      kvps={packageDetails.kvps}
                      disabled={!bidEditMode}
                      className={classes.select}
                    />
                  </Grid>
                )}
                <Grid item>
                  <DeliveryAddressSelect
                    name="deliveryAddress"
                    rules={{ required: true }}
                    control={control}
                    deliveryAddresses={setToArray(packageDetails.deliveryAddressSelection?.deliveryAddresses).filter(
                      (address) => (isNoKVPSCar ? true : allowedDeliveryAddressIds.includes(address.id as string)),
                    )}
                    disabled={!bidEditMode}
                    className={classes.select}
                    setValue={setValue}
                  />
                </Grid>
                {packageDetails.source === SourceType.Vwfs && noCentralKvpsSelected && (
                  <Grid item>
                    <TransportSelectPackage
                      options={transportOptionsData?.options}
                      loading={transportOptionsLoading}
                      disabled={!bidEditMode || !watchDeliveryAddress || !watchKvps}
                      className={classes.select}
                    />
                  </Grid>
                )}
              </Grid>
            </div>
          </div>
          <div className={classes.printHide}>
            <BidBuyTimer car={carForComponents} refresh={() => Promise.resolve(refresh())} />
          </div>
        </div>

        <BidAlerts
          car={carForComponents}
          errors={errors}
          className={classes.bidAlerts}
          supportsBidding={supportsBidding}
          supportsBuying={supportsBuying}
        />

        {supportsBidding &&
          packageDetails.source &&
          packageDetails.packageId &&
          actualBidSuccessState !== BidSuccessStatus.Bought && (
            <BidForm car={carForComponents} bidEditMode={bidEditMode} fetchCalculation={fetchCalculationForBidForm} />
          )}

        <BidBuySubmit
          car={carForComponents}
          supportsBuying={supportsBuying}
          supportsBidding={supportsBidding}
          supportsBidAutomation={supportsBidAutomation}
          buyPrice={packageDetails.potential?.base?.amount}
          actualBidSuccessState={actualBidSuccessState}
          onSuccess={() => setBidEditMode(false)}
          refresh={() => {
            // Delay refresh to give the backend time to process the bid before refetching
            setTimeout(() => refresh(), 4000);
          }}
          bidEditMode={bidEditMode}
          errors={errors}
          transportOptionsLoading={transportOptionsLoading}
        />
      </Paper>
      {bidForDeletion && (
        <DeleteBidDialog
          onClose={onClose}
          bidForDeletion={bidForDeletion}
          reload={() => {
            setBidEditMode(true);
            setBidDeletePending(true);
            // Longer delay for deletion — backend takes more time to update bid state after delete
            setTimeout(() => refresh(), 6000);
          }}
          carsCount={packageDetails.cars?.length}
        />
      )}
    </FormProvider>
  );
};

export default BidBuyPackage;
